import classNames from 'classnames';
import { ErrorMessage, Field, useField } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import i18n from '../../i18n';
import { ucFirst } from '../../utils/caseconverters';
import EventIcon from '../EventIcon';
import Icon from '../Icon';
import styles from './InputGroup.module.scss';

const InputGroup = ({
    name = null,
    id = null,
    label = null,
    infoMessage = null,
    children = null,
    modifiers = [],
    type,
    items,
    placeholder,
    extraErrorMessage,
    autoComplete,
    i18nlabel,
    ...rest
}) => {
    const [field, meta, helpers] = useField(name);
    const textareaRef = React.useRef();

    const Tag = type === 'checkbox' ? 'div' : 'label';
    return (
        <Tag
            className={classNames(styles['InputGroup'], modifiers, {
                [styles['InputGroup--Error']]: meta.touched && meta.error,
                [styles['InputGroup--Textarea']]: type === 'textarea',
                [styles['InputGroup--File']]: type === 'file',
            })}>
            {type === 'checkbox' && (
                <Checkbox name={name} items={items} i18nlabel={i18nlabel} />
            )}
            {type === 'radio' && <Radio name={name} items={items} />}
            {label && (
                <span
                    className={classNames(styles['InputGroup__FieldLabel'], {
                        [styles['InputGroup__FieldLabel--Checkbox']]:
                            type === 'checkbox',
                    })}>
                    {label}
                </span>
            )}
            {!(type === 'checkbox' || type === 'radio') && (
                <>
                    {type === 'textarea' && (
                        <Field
                            as="textarea"
                            type={type}
                            name={name}
                            placeholder={placeholder}
                            innerRef={textareaRef}
                            className={
                                styles[
                                    `InputGroup--${ucFirst(
                                        type || 'Field'
                                    )}Field`
                                ]
                            }
                            onKeyUp={() => {
                                textareaRef.current.style.height = 'auto';
                                textareaRef.current.style.height =
                                    textareaRef.current.scrollHeight + 'px';
                            }}
                            {...rest}
                        />
                    )}
                    {type !== 'textarea' && (
                        <Field
                            type={type}
                            name={name}
                            className={
                                styles[
                                    `InputGroup--${ucFirst(
                                        type || 'Field'
                                    )}Field`
                                ]
                            }
                            autoComplete={autoComplete}
                            placeholder={placeholder}
                            {...rest}
                        />
                    )}
                </>
            )}
            {infoMessage && (
                <div className={styles['InputGroup__InfoMessage']}>
                    {infoMessage}
                </div>
            )}

            <ErrorMessage name={name} component={Error} />

            {!!extraErrorMessage && (
                <Error>
                    <p>{extraErrorMessage}</p>
                </Error>
            )}
        </Tag>
    );
};

const Checkbox = (props) => {
    const items = props.items || [{ value: props.name, label: props.name }];
    const i18nl = props.i18nlabel ? i18n.t(props.i18nlabel) : null;
    return (
        <div role="group" className={styles['InputGroup__Checkbox']}>
            {items.map((item, i) => (
                <label key={i} className={styles['InputGroup__CheckboxLabel']}>
                    <Field
                        type="checkbox"
                        role="checkbox"
                        name={item.name || item.value}
                        value={item.value}
                    />
                    <span className={styles['InputGroup__CheckboxMark']} />
                    {!!item.icon && <EventIcon icon={item.icon} size="small" />}
                    {i18nl && (
                        <div className={styles['InputGroup__CheckboxText']}>
                            {i18nl}
                        </div>
                    )}
                    {!i18nl && (
                        <span
                            className={styles['InputGroup__CheckboxText']}
                            dangerouslySetInnerHTML={{ __html: item.label }}
                        />
                    )}
                </label>
            ))}
        </div>
    );
};

const Radio = (props) => {
    const items = props.items || [{ value: props.name, label: props.name }];
    return (
        <div className={styles['InputGroup__Radio']} role="radiogroup">
            {items.map((item) => (
                <label className={styles['InputGroup__RadioLabel']}>
                    <Field type="radio" name={props.name} value={item.value} />
                    <span className={styles['InputGroup__RadioMark']} />
                    <span className={styles['InputGroup__RadioText']}>
                        {item.label}
                    </span>
                </label>
            ))}
        </div>
    );
};

const DropDown = (name, options, placeholder) => {
    return (
        <Field as="select" name={name} placeholder={placeholder}>
            {options.length &&
                options.map((item) => {
                    <option value={item}>{item}</option>;
                })}
        </Field>
    );
};
export const Error = ({ children }) => {
    const id = btoa(children);
    return (
        <div
            tabIndex="0"
            role="alert"
            aria-live="polite"
            aria-invalid="true"
            aria-describedby={`error_message_${id}`}
            className={styles['InputGroup__ErrorMessage']}>
            <Icon type="error" size="small" color="error" />
            <p
                id={`error_message_${id}`}
                className={styles['InputGroup__ErrorMessageText']}>
                {children}
            </p>
        </div>
    );
};

InputGroup.propTypes = {
    error: PropTypes.object,
    touched: PropTypes.object,
    name: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    infoMessage: PropTypes.string,
    children: PropTypes.any,
    modifiers: PropTypes.array,
};

export default InputGroup;
